import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Container, Row, Col, Alert, ProgressBar, Form, InputGroup, ListGroup, Image as BootstrapImage } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import CardList from './CardList';
import CardModal from './CardModal';
import CardPicker from './CardPicker';
import { useUser } from '../UserContext';
import { buildUrlWithParams } from './utils.js';
import ContestPhaseIndicator from './ContestPhaseIndicator';
import './Contest.css';

// Constants for Circle Sizing
const DEFAULT_CIRCLE_SIZE = 40; // px - The default/average size
const MIN_CIRCLE_SIZE = 25;     // px - Smallest circle
const MAX_CIRCLE_SIZE = 80;     // px - Largest circle

// Color mappings (ensure these are defined as in previous correct examples)
const RARITY_COLORS = {
  "Legendary": "#ff8c00", "Epic": "#a335ee", "Rare": "#0070dd",
  "Common": "#adb5bd", "Free": "#6c757d", "Unknown Rarity": "#343a40"
};
const CLASS_COLORS = {
  "Death Knight": "#70A4D2", "Demon Hunter": "#A330C9", "Druid": "#FF7D0A",
  "Hunter": "#ABD473", "Mage": "#3FC7EB", "Paladin": "#F58CBA",
  "Priest": "#E8E8E8", "Rogue": "#FFF569", "Shaman": "#0070DE",
  "Warlock": "#8787ED", "Warrior": "#C79C6E", "Neutral": "#808080",
  "Unknown Class": "#343a40"
};
const DEFAULT_STACK_COLORS = ['#0d6efd', '#198754', '#0dcaf0', '#ffc107', '#dc3545', '#6c757d', '#fd7e14', '#20c997', '#6610f2', '#d63384'];

function getTextColorForBackground(hexColor) {
  if (!hexColor || hexColor.length < 7) return '#000000';
  try {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#000000' : '#FFFFFF';
  } catch (e) { return '#000000'; }
}

// --- END OF DEFINITIONS ---

// Utility function to determine contest phase
const getContestPhase = (startDate, submissionsEndDate, endDate) => {
  const currentTime = Math.floor(Date.now() / 1000); // Current time in Unix timestamp

  if (currentTime < startDate) {
    return 'not_started';
  } else if (currentTime >= startDate && currentTime < submissionsEndDate) {
    return 'submission';
  } else if (currentTime >= submissionsEndDate && currentTime < endDate) {
    return 'voting';
  } else {
    return 'cooldown';
  }

  // return 'voting';

};

const PREDEFINED_THEMES = [ 
  { id: 'theme_1', name: 'Mythic Creatures' },
  { id: 'theme_2', name: 'Elemental Fury' },
  { id: 'theme_3', name: 'Clockwork Contraptions' },
  { id: 'theme_4', name: 'Shadow Magic' },
  { id: 'theme_5', name: 'Heroic Legends' },
];

const memeMessages = [
  "Sorry, but this contest is in another castle.",
  "These aren't the contests you're looking for.",
  "An Illusion what are you hiding?",
  "It's a Secret to Everybody.",
  "Are You Winning, Son?",
  "Contest isn’t ready yet? You’re not thinking fourth-dimensionally!",
  "This is fine.",
  "This is the way."
];

const randomMessage = memeMessages[Math.floor(Math.random() * memeMessages.length)];

const Contest = () => {
  const { id } = useParams();
  const [contestInfo, setContestInfo] = useState(null);
  const [userSubmission, setUserSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [themeCards, setThemeCards] = useState([]);

  // --- New State for Winner Theme Suggestion ---
  const [themeMethod, setThemeMethod] = useState('type'); // 'type', 'select', 'pass'
  const [typedThemes, setTypedThemes] = useState(['', '', '']);
  const [selectedPredefinedTheme, setSelectedPredefinedTheme] = useState('');
  const [winnerHasSubmittedTheme, setWinnerHasSubmittedTheme] = useState(false); // Should be fetched
  const [submittedWinnerThemesInfo, setSubmittedWinnerThemesInfo] = useState(null); // Store what was submitted { method: 'type', themes: [...] }
  const [isSubmittingTheme, setIsSubmittingTheme] = useState(false); // Loading state for submit button
  // --- End New State ---

  const [submissionStats, setSubmissionStats] = useState(null);

  const { user, setUser, selectedCard, setSelectedCard, settings, setSettings, cards, setCards } = useUser();

  useEffect(() => {
    fetchContestInfo();
    fetchSubmissions();
    if (user) {
      fetchUserSubmission();
    } else {
      setUserSubmission(null);
    }
  }, [user, id]);

  // Update phase every minute
  useEffect(() => {
    if (contestInfo) {
      const updatePhase = () => {
        const phase = getContestPhase(
          contestInfo.start_date,
          contestInfo.submissions_end_date,
          contestInfo.end_date
        );
        setCurrentPhase(phase);
      };

      updatePhase(); // Initial phase set
      const interval = setInterval(updatePhase, 1000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [contestInfo]);

  // Fetch theme cards only when in voting phase
  useEffect(() => {
    if (currentPhase === 'voting' || currentPhase === 'cooldown') {
      fetchThemeCards();
    }
  }, [contestInfo, currentPhase]);

  // --- New useEffect for calculating submission stats ---
  useEffect(() => {
    if (currentPhase === 'cooldown' && submissions && submissions.length > 0) {
        const calculateStats = () => {
            const classCounts = {};
            const rarityCounts = {};
            const manaCounts = {};

            submissions.forEach(sub => {
                // Class: data like "Mage", "Warrior", "Neutral"
                const subClass = sub.class || 'Unknown Class';
                classCounts[subClass] = (classCounts[subClass] || 0) + 1;

                // Rarity (gem): data like "Legendary", "Epic", "Rare", "Common", "Free"
                const subRarity = sub.gem || 'Unknown Rarity';
                rarityCounts[subRarity] = (rarityCounts[subRarity] || 0) + 1;

                // Mana: data like 0, 1, 5, 10
                if (typeof sub.mana === 'number' && !isNaN(sub.mana)) {
                    const manaVal = parseInt(sub.mana, 10);
                    const manaKey = manaVal >= 10 ? '10+' : String(manaVal);
                    manaCounts[manaKey] = (manaCounts[manaKey] || 0) + 1;
                } else {
                    manaCounts['Unknown Mana'] = (manaCounts['Unknown Mana'] || 0) + 1;
                }
            });

            // Sort mana keys numerically, with '10+' and 'Unknown Mana' at the end
            const sortedManaKeys = Object.keys(manaCounts).sort((a, b) => {
                const isAUknown = a === 'Unknown Mana';
                const isBUknown = b === 'Unknown Mana';
                const isA10Plus = a === '10+';
                const isB10Plus = b === '10+';

                if (isAUknown) return 1; // Unknown Mana always last
                if (isBUknown) return -1;
                if (isA10Plus && !isB10Plus && !isBUknown) return 1; // 10+ after numbers but before Unknown
                if (isB10Plus && !isA10Plus && !isAUknown) return -1; // numbers before 10+
                if (isA10Plus && isB10Plus) return 0; // both are 10+

                return parseInt(a) - parseInt(b); // Sort numerical keys
            });

            const sortedManaCounts = {};
            sortedManaKeys.forEach(key => {
                sortedManaCounts[key] = manaCounts[key];
            });

            setSubmissionStats({
                classCounts,
                rarityCounts,
                manaCounts: sortedManaCounts,
                totalSubmissions: submissions.length
            });
        };
        calculateStats();
    } else {
        setSubmissionStats(null); // Clear stats if not in cooldown or no submissions
    }
  }, [currentPhase, submissions]);
  // --- End new useEffect ---

  const fetchContestInfo = async () => {
    try {
      const response = await axios.get(`/api/contest/get_contest_info.php?id=${id}`);
      setContestInfo(response.data);
      // Set initial phase
      setCurrentPhase(getContestPhase(
        response.data.start_date,
        response.data.submissions_end_date,
        response.data.end_date
      ));
    } catch (error) {
      console.error('Error fetching contest info:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`/api/contest/get_submissions.php?id=${id}`);
      //console.log(response.data);
      setSubmissions(response.data);
      const updatedData = response.data.map(submission => ({
        ...submission,
        id: String(submission.id), // Ensure id is a string
      }));
      setCards(updatedData);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchUserSubmission = async () => {
    try {
      const response = await axios.get(`/api/contest/user-submission.php?contest_id=${id}&user_id=${user.userid}`);
      setUserSubmission(response.data);
    } catch (error) {
      console.error('Error fetching user submission:', error);
    }
  };

  const fetchThemeCards = async () => {
    try {
      const response = await axios.get(`/api/contest/get_theme_cards.php?id=${id}`);
      // The response should return an array of theme objects, e.g.,
      // [{ id: 1, name: 'Nature', image: 'nature.webp', votes: 10, votePercentage: 40 }, ...]
      setThemeCards(response.data);
    } catch (error) {
      console.error('Error fetching theme cards:', error);
    }
  };

  const handleWithdraw = async () => {
    try {
      await axios.post(`/api/contest/withdraw-card.php`, { contestId: id, userId: user.userid });
      setUserSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error withdrawing card:', error);
    }
  };

  const handleSubmit = async (cardId) => {
    try {
      console.log(id);
      await axios.post(`/api/contest/submit-card.php`, { contestId: id, userId: user.userid, cardId: cardId });
      fetchUserSubmission();
      fetchSubmissions();
    } catch (error) {
      console.error('Error submitting card:', error);
    }
  };

  const handleVote = async (cardId) => {
    try {
      await axios.post(`/api/contest/vote/${id}`, { userId: user.userid, cardId });
      fetchSubmissions();
    } catch (error) {
      console.error('Error voting for card:', error);
    }
  };

  const handleThemeVote = async (themeId) => {
    try {
      await axios.post(`/api/contest/vote_theme.php`, { contestId: id, themeId: themeId });
      // Optionally, refresh the theme cards after voting to update vote counts
      fetchThemeCards();
    } catch (error) {
      console.error('Error voting for theme:', error);
    }
  };

    // --- New Handler Functions ---
    const handleTypedThemeChange = (index, value) => {
      const newThemes = [...typedThemes];
      newThemes[index] = value;
      setTypedThemes(newThemes);
  };

  const handleWinnerThemeSubmit = async () => {
    setIsSubmittingTheme(true);
    let submissionData = {
        contestId: id, // ID of the contest they won
        userId: user.userid,
        method: themeMethod,
        themes: [],
    };

    if (themeMethod === 'type') {
        submissionData.themes = typedThemes.map(t => t.trim()).filter(t => t !== ''); // Only non-empty themes
        if (submissionData.themes.length === 0) {
            alert("Please enter at least one theme suggestion.");
            setIsSubmittingTheme(false);
            return;
        }
    } else if (themeMethod === 'select') {
        if (!selectedPredefinedTheme) {
            alert("Please select a predefined theme.");
            setIsSubmittingTheme(false);
            return;
        }
        submissionData.themes = [selectedPredefinedTheme]; // Send the ID or name
    } else if (themeMethod === 'pass') {
         // Themes array remains empty, method is 'pass'
    }

    console.log("Submitting winner theme:", submissionData);

    try {
        // *** TODO: Replace with actual API call ***
        // const response = await axios.post(`/api/contest/submit_winner_theme.php`, submissionData);
        // console.log("Winner theme submission response:", response.data);

        // --- Placeholder Logic ---
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        console.log("Winner theme submitted successfully (placeholder).");
        setWinnerHasSubmittedTheme(true);
        // Store a simplified version of what was submitted for display
        const submittedInfo = { method: themeMethod, themes: [] };
        if (themeMethod === 'type') submittedInfo.themes = submissionData.themes;
        if (themeMethod === 'select') {
           const selectedThemeObj = PREDEFINED_THEMES.find(t => t.id === selectedPredefinedTheme);
           submittedInfo.themes = selectedThemeObj ? [selectedThemeObj.name] : [selectedPredefinedTheme];
        }
        if (themeMethod === 'pass') submittedInfo.themes = ["Passed"];
        setSubmittedWinnerThemesInfo(submittedInfo);
        // --- End Placeholder Logic ---

    } catch (error) {
        console.error("Error submitting winner theme:", error);
        alert("There was an error submitting your theme suggestion. Please try again.");
    } finally {
        setIsSubmittingTheme(false);
    }
};

// end

  const renderSubmissionContent = () => {
    if (!user) {
      return (
        <Col lg={5} className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <p className="lead">
              To submit a card, please log in.
            </p>
          </div>
        </Col>
      );
    }

    // Only allow submissions during submission phase
    if (currentPhase !== 'submission') {
      return (
        <Col lg={5} className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <p className="lead">
              The submission phase has ended.
            </p>
          </div>
        </Col>
      );
    }

    return (
      <Col lg={5} className="d-flex align-items-center justify-content-center">
        {userSubmission ? (
          <div className="text-center">
            <Link
              to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: userSubmission.cardId })}
              preventScrollReset={true}
              style={{ textDecoration: 'none', zIndex: '2' }}
            >
              <img
                src={`/cards/${userSubmission.cardId2}.webp`}
                alt="Your submission"
                className="img-fluid mb-3 submission-card"
                style={{ maxWidth: '300px' }}
              />
            </Link>
            <div>
              <Button variant="danger" onClick={handleWithdraw}>
                Withdraw Submission
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={() => setShowCardPicker(true)}
            className="contest-submit-button"
          >
            <p className="fs-1 mb-0">+</p>
            <p className="mt-0">Submit a Card</p>
          </Button>
        )}
      </Col>
    );
  };


// --- New Rendering Function ---
const renderWinnerThemeSection = () => {
  const isWinner = true;
  const winnerUsername = "Nikko";

  // --- 1. Placeholder during Submission/Voting ---
  if (currentPhase === 'submission' || currentPhase === 'voting') {
      return (
          <Col lg={10} className="mt-4 mb-4">
              <div className="winner-reward-placeholder text-center p-4 border rounded shadow-sm">
                  <img src="/images/contest/reward_chest.webp" alt="Reward Chest" className="mb-3" style={{ maxHeight: '100px', opacity: '0.7' }}/>
                  <h4>Winner's Reward!</h4>
                  <p className="lead mb-0">The winner of this contest gets to suggest the theme for next week's contest!</p>
              </div>
          </Col>
      );
  }

  // --- 2. Cooldown Phase Logic ---
  if (currentPhase === 'cooldown') {
      // Don't show if there's no winner declared yet
      if (!contestInfo?.winners || contestInfo.winners.length === 0) {
          return null;
      }

      // --- 2a. If current user IS the winner ---
      if (isWinner) {
          if (winnerHasSubmittedTheme) {
              // Winner already submitted
              return (
                  <Col lg={9} className="mt-5 pb-3">
                      <h3 className="text-center mb-3">Next Week's Theme Suggestion</h3>
                      <Alert variant="success" className="text-center">
                          <Alert.Heading>Thank You!</Alert.Heading>
                          <p>Your theme suggestion for the next contest has been submitted.</p>
                          {submittedWinnerThemesInfo && (
                              <>
                                  <hr />
                                  <p className="mb-1"><strong>Method:</strong> {submittedWinnerThemesInfo.method}</p>
                                  {submittedWinnerThemesInfo.themes && submittedWinnerThemesInfo.themes.length > 0 && (
                                      <p className="mb-0"><strong>Suggestion(s):</strong> {submittedWinnerThemesInfo.themes.join(', ')}</p>
                                  )}
                              </>
                          )}
                      </Alert>
                  </Col>
              );
          } else {
              // Winner needs to submit - Show the form
              return (
                <Container fluid className="winner-theme-submission-container">
                <Row className="align-items-center">
                <h3 className="text-center mb-3">Pick Next Week's Theme!</h3>
                <p className="text-center lead pb-5">As the winner of this contest, you get to influence the next contest's theme.</p>

                    {/* Column 1: Reward Image */}
                    <Col md={4} lg={4} className="text-center text-md-start mb-4 mb-md-0 pe-md-4 align-self-start">
                        <BootstrapImage
                            src="/images/contest/chest-open3.webp" // Adjust path as needed
                            alt="Reward Chest"
                            fluid
                            className="reward-chest-image img-fluid" // Add custom class if needed for specific styling
                        />
                        <p className="lead text-center">You have 5 days to submit your choice.</p>
                    </Col>
    
                    {/* Column 2: Form Content */}
                    <Col md={8} lg={8} className="winner-theme-submission-form ps-md-4 align-self-start">

    
                        <Form onSubmit={(e) => e.preventDefault()}>
    
                            {/* --- Image Selector for Method --- */}
                            <Form.Group as={Row} className="mb-4 justify-content-center text-center theme-method-selector">
                                <Col xs={12} className="mb-3">
                                    <Form.Label className="h5">Choose how you wish to proceed:</Form.Label>
                                </Col>
                                {/* Option 1: Type */}
                                <Col xs={4} md={4}>
                                    <div
                                        className={`theme-option ${themeMethod === 'type' ? 'selected' : ''}`}
                                        onClick={() => setThemeMethod('type')}
                                        role="button" // Better accessibility
                                        tabIndex={0} // Make it focusable
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setThemeMethod('type')} // Keyboard activation
                                    >
                                        <BootstrapImage
                                            src="/images/contest/contest-winner-option-type.webp" // ADJUST PATH
                                            alt="Type your own theme"
                                            fluid
                                            className="theme-option-image"
                                        />
                                        {/* <p className="mt-2 mb-0 option-label">Type</p> */}
                                    </div>
                                </Col>
                                {/* Option 2: Select */}
                                <Col xs={4} md={4}>
                                    <div
                                        className={`theme-option ${themeMethod === 'select' ? 'selected' : ''}`}
                                        onClick={() => setThemeMethod('select')}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setThemeMethod('select')}
                                    >
                                        <BootstrapImage
                                            src="/images/contest/contest-winner-option-select.webp" // ADJUST PATH
                                            alt="Select from list"
                                            fluid
                                            className="theme-option-image"
                                        />
                                        {/* <p className="mt-2 mb-0 option-label">Select</p> */}
                                    </div>
                                </Col>
                                {/* Option 3: Pass */}
                                <Col xs={4} md={4}>
                                    <div
                                        className={`theme-option ${themeMethod === 'pass' ? 'selected' : ''}`}
                                        onClick={() => setThemeMethod('pass')}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setThemeMethod('pass')}
                                    >
                                        <BootstrapImage
                                            src="/images/contest/contest-winner-option-pass.webp" // ADJUST PATH
                                            alt="Pass / Let system choose"
                                            fluid
                                            className="theme-option-image"
                                        />
                                        {/* <p className="mt-2 mb-0 option-label">Pass</p> */}
                                    </div>
                                </Col>
                            </Form.Group>
    
                            {/* --- Conditional Inputs --- */}
                            {themeMethod === 'type' && (
                              <>
                                <h3>Enter the Theme(s)</h3>
                                <Form.Group as={Row} className="mb-3 justify-content-center fade-in">
                                    <Col sm={10} md={9} lg={10}>
                                        <InputGroup className="mb-2">
                                            <InputGroup.Text>1.</InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Theme Suggestion 1 (Required)"
                                                value={typedThemes[0]}
                                                onChange={(e) => handleTypedThemeChange(0, e.target.value)}
                                                maxLength={100}
                                                className="hstone-input"
                                                required // Add basic HTML5 validation
                                            />
                                        </InputGroup>
                                        <Form.Text muted className="d-block mb-2">
                                            If you provide more than one, users will vote to choose what they like more.
                                        </Form.Text>
                                        <InputGroup className="mb-2">
                                            <InputGroup.Text>2.</InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Theme Suggestion 2 (Optional)"
                                                value={typedThemes[1]}
                                                onChange={(e) => handleTypedThemeChange(1, e.target.value)}
                                                maxLength={100}
                                                                                                className="hstone-input"
                                            />
                                        </InputGroup>
                                        <InputGroup>
                                            <InputGroup.Text>3.</InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Theme Suggestion 3 (Optional)"
                                                value={typedThemes[2]}
                                                onChange={(e) => handleTypedThemeChange(2, e.target.value)}
                                                maxLength={100}
                                                                                                className="hstone-input"
                                            />
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                </>
                            )}
    
                            {themeMethod === 'select' && (
                              <>
                                <h3>Choose from a List</h3>
                                <Form.Group as={Row} className="mb-3 justify-content-center fade-in">
                                    <Col sm={10} md={9} lg={9}>
                                        <Form.Select
                                            aria-label="Select predefined theme"
                                            value={selectedPredefinedTheme}
                                            onChange={(e) => setSelectedPredefinedTheme(e.target.value)}
                                            required // Add basic HTML5 validation
                                        >
                                            <option value="">-- Select a Theme --</option>
                                            {PREDEFINED_THEMES.map(theme => (
                                                <option key={theme.id} value={theme.id}>{theme.name}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text muted className="d-block mt-1">
                                            This theme will be used for the next contest directly.
                                        </Form.Text>
                                    </Col>
                                </Form.Group>
                                </>
                            )}
    
                            {themeMethod === 'pass' && (
                              <>
                              <h3>Pass</h3>
                                <p className="text-center text-muted mb-3 fade-in">
                                    The system will automatically select a theme for the next contest.
                                </p>
                                </>
                            )}
    
                            {/* --- Submit Button --- */}
                            {/* Only show submit button if a method is chosen */}
                            {themeMethod && (
                                 <div className="text-center mt-4 fade-in">
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        onClick={handleWinnerThemeSubmit}
                                        disabled={{}} // Use helper function
                                        className="hstone-button"
                                    >
                                        {isSubmittingTheme ? 'Submitting...' : 'Submit Choice'}
                                    </Button>
                                </div>
                            )}
                        </Form>
                    </Col>
                </Row>
            </Container>
              );
          }
      }
      // --- 2b. If current user is NOT the winner ---
      else {
           // Check if the winner *has* submitted something to display (optional)
           // This requires fetching the submitted theme info even for non-winners
           // Let's assume `submittedWinnerThemesInfo` might be fetched regardless in `contestInfo`

          // Placeholder text if not the winner
          return (
              <Col lg={9} className="mt-5 pb-3 text-center">
                  <h3 className="mb-3">Next Week's Theme</h3>
                  <p className="lead">
                      Congratulations to the winner, <Link to={`/user/${contestInfo.winners[0].id}`}>{winnerUsername}</Link>!
                  </p>
                  <p>They get to suggest the theme for the next contest.</p>
                  {/* Optional: Display what they chose if available
                  {submittedWinnerThemesInfo && winnerHasSubmittedTheme && (
                       <Alert variant="info">
                         <p className="mb-1"><strong>Chosen Method:</strong> {submittedWinnerThemesInfo.method}</p>
                         {submittedWinnerThemesInfo.themes && submittedWinnerThemesInfo.themes.length > 0 && (
                            <p className="mb-0"><strong>Suggestion(s):</strong> {submittedWinnerThemesInfo.themes.join(', ')}</p>
                         )}
                      </Alert>
                  )}
                  */}
              </Col>
          );
      }
  }

  // --- 3. Default: Return null for other phases/cases ---
  return null;
};
// --- End New Rendering Function ---


  // --- MODIFIED Rendering Function for Submission Stats ---
  const renderSubmissionStats = () => {
    if (!submissionStats || submissionStats.totalSubmissions === 0) {
        return (
             <Col xs={12} className="mt-5 mb-4"> {/* Ensure this Col exists for spacing consistency */}
                 <p className="text-center text-muted">No submission data available to analyze for detailed stats.</p>
            </Col>
        );
    }

    const { classCounts, rarityCounts, manaCounts, totalSubmissions } = submissionStats;

    const sortByCountDescAndKey = ([keyA, countA], [keyB, countB]) => {
        if (countB !== countA) return countB - countA; // Sort by count descending
        return keyA.localeCompare(keyB); // Then alphabetically for ties
    };

    // Helper to render each category as a row of "bubbles"
    const renderStatCategoryBubbles = (title, counts, categoryKey, sortFn) => {
        if (Object.keys(counts).length === 0) return null;

        let itemsToRender = Object.entries(counts);
        if (sortFn) {
            itemsToRender.sort(sortFn);
        }

        const percentages = itemsToRender.map(([, count]) => (count / totalSubmissions) * 100);
        const minPercentage = percentages.length > 0 ? Math.min(...percentages) : 0;
        const maxPercentage = percentages.length > 0 ? Math.max(...percentages) : 0;

        let colorMap = {};
        if (categoryKey === 'rarity') colorMap = RARITY_COLORS;
        else if (categoryKey === 'class') colorMap = CLASS_COLORS;

        return (
            <div className="mb-1 submission-stat-category-bubbles-row">
                {/* <h5 className="mb-3 text-center">{title}</h5> */}
                <Row className="justify-content-center">
                    <Col xs={12} md={11} lg={12} className="d-flex flex-wrap justify-content-center align-items-center">
                        {/* align-items-start or align-items-baseline can be chosen. 
                            'start' aligns tops of .stat-bubble containers
                            'baseline' aligns based on first line of text in .stat-bubble (the circle content) */}
                        {itemsToRender.map(([key, count], index) => {
                            const percentage = (count / totalSubmissions) * 100;

                            let currentCircleSize = DEFAULT_CIRCLE_SIZE;
                            if (maxPercentage > minPercentage && maxPercentage > 0) {
                                const range = maxPercentage - minPercentage;
                                const relativePos = (percentage - minPercentage) / range; // 0 to 1
                                // Apply a non-linear scale (e.g., sqrt) to make differences less extreme
                                const sizeVariation = (MAX_CIRCLE_SIZE - MIN_CIRCLE_SIZE) * Math.sqrt(Math.max(0, relativePos)); // Ensure non-negative for sqrt
                                currentCircleSize = MIN_CIRCLE_SIZE + sizeVariation;
                            } else if (itemsToRender.length === 1 && percentage > 0) {
                                currentCircleSize = (MIN_CIRCLE_SIZE + MAX_CIRCLE_SIZE) / 2;
                            }
                            currentCircleSize = Math.max(MIN_CIRCLE_SIZE, Math.min(MAX_CIRCLE_SIZE, currentCircleSize));

                            let segmentColor = colorMap[key] || DEFAULT_STACK_COLORS[index % DEFAULT_STACK_COLORS.length];
                            if (categoryKey === 'mana') {
                                 const manaVal = parseInt(key);
                                 if (!isNaN(manaVal)) segmentColor = DEFAULT_STACK_COLORS[manaVal % DEFAULT_STACK_COLORS.length];
                                 else if (key === '10+') segmentColor = DEFAULT_STACK_COLORS[10 % DEFAULT_STACK_COLORS.length];
                                 else segmentColor = '#343a40';
                            }
                            
                            const textColorInBubble = getTextColorForBackground(segmentColor);
                            // For placeholder, using percentage. When images come, this text might be removed or become an overlay.
                            const bubbleContent = `${percentage.toFixed(0)}%`; 

                            // Dynamic font size based on circle size for better fit of content
                            let bubbleFontSize = '0.75rem';
                            if (currentCircleSize > MAX_CIRCLE_SIZE - 10) bubbleFontSize = '0.9rem';
                            else if (currentCircleSize < MIN_CIRCLE_SIZE + 10) bubbleFontSize = '0.65rem';


                            return (
                                <div key={key} className="stat-bubble" title={`${key}: ${count} (${percentage.toFixed(1)}%)`}>
                                    <div
                                        className="stat-bubble-circle"
                                        style={{
                                            width: `${currentCircleSize}px`,
                                            height: `${currentCircleSize}px`,
                                            backgroundColor: segmentColor,
                                            color: textColorInBubble,
                                            fontSize: bubbleFontSize 
                                            // If using images:
                                            // backgroundImage: `url('/path/to/image/${key}.png')`,
                                        }}
                                    >
                                        {bubbleContent} {/* This is the placeholder text */}
                                    </div>
                                    <span className="stat-bubble-label">{key == 'DemonHunter' ? 'DH' : key}</span>
                                </div>
                            );
                        })}
                    </Col>
                </Row>
            </div>
        );
    };
    
    return (
        <> {/* This fragment is important as it's directly placed inside a Col lg={10} */}
            <h3 className="text-center mb-3 mt-5">Submission Statistics</h3>
            
            {renderStatCategoryBubbles("Class Distribution", classCounts, 'class', sortByCountDescAndKey)}
            {renderStatCategoryBubbles("Rarity Distribution", rarityCounts, 'rarity', sortByCountDescAndKey)}
            {renderStatCategoryBubbles("Mana Cost Distribution", manaCounts, 'mana', null)} {/* Mana is pre-sorted */}
        </>
    );
  };
  // --- End MODIFIED Rendering Function ---

  const renderPhaseContent = () => {
    switch (currentPhase) {
      case 'not_started':
        return (
          <div className="text-center">
            <h2 className="mb-4">Contest Not Started</h2>
            <Alert variant="info" className="mx-5 filter">
              <p className="mb-0">
                This contest will begin on {new Date(contestInfo.start_date * 1000).toLocaleString()}
              </p>
            </Alert>
          </div>
        );
      case 'submission':
        return (
          <>
            <Col lg={10}>
              <Row className="pt-lg-3 mb-4">
                <Col lg={7} className="mb-4 mb-lg-0">
                  <h3 className="mb-3">Submission Rules</h3>
                  <ul className="lead text-start">
                    <li>You may submit <strong>one</strong> card.</li>
                    <li>If the card has <strong>tokens</strong>, they should be <strong>attached to the card</strong>.</li>
                    <li>You can <strong>attach tokens or edit the card by clicking on it</strong> after submitting it.</li>
                    <li>During the submission phase, you'll be able to edit or completely change your submission freely.</li>
                    <li>Once the Voting Phase begins, your submission will be locked.</li>
                    <li>After the Voting Phase, card with the most likes will be declared as the Winner.</li>
                  </ul>
                </Col>
                {renderSubmissionContent()}
              </Row>

              {/* <hr className="my-5" style={{ borderWidth: '3px' }} /> */}
              {/* <img src="https://www.hearthcards.net/images/contest/separator.webp" className="img-fluid my-3 mb-5" /> */}

              <div className="custom-separator my-5">
                <div class="separator-end"></div><div class="separator-middle"></div><div class="separator-end right"></div>
              </div>
            </Col>

            <h3 className="mb-1">Submissions</h3>
            <p className="text-center lead mb-4">Voting will open once the Submission Phase ends.</p>
            <CardList
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
              onVote={currentPhase === 'voting' && user ? handleVote : null}
              settings={settings}
            />
          </>
        );
      case 'voting':
        return (
          <>
            <Col lg={10}>
              <h2 className="text-center mb-1">Voting Phase</h2>
              <p className="text-center lead pb-3">Vote for your favorite cards below!</p>
            </Col>
            <CardList
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
              onVote={currentPhase === 'voting' && user ? handleVote : null}
              settings={settings}
            />

{themeCards.length > 0 && (
              <>
                <Col lg={10}>
                  <div className="custom-separator my-5">
                    <div className="separator-end"></div>
                    <div className="separator-middle"></div>
                    <div className="separator-end right"></div>
                  </div>
                </Col>
                <Col lg={9} className="mt-1 pb-3">
                  <h2 className="text-center mb-3 pb-5">Vote for Next Week's Theme</h2>
                  <Row>
                    {themeCards.map((theme) => (
                      <Col md={4} key={theme.id} className="mb-4">
                        <div className="contest-theme-styling theme-card d-flex flex-column h-100 text-center p-3">
                          <img
                            src={`/images/contest/sketch/${theme.image ? theme.image : "2"}.svg`}
                            className="w-100"
                            alt="theme"
                          />
<hr className="" />
                          <h5 className="my-1">{theme.theme}</h5>
                          <div className="mt-auto">

                            <Button
                              variant={theme.voted ? "success" : "primary"}
                              className="w-100 mt-2"
                              onClick={() => handleThemeVote(theme.id)}
                            >
                              {theme.voted ? "Voted" : "Vote"}
                            </Button>


                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              </>
            )}

          </>
        );
      case 'cooldown':
        const maxVotes = themeCards.length > 0 ? Math.max(...themeCards.map(theme => theme.votes)) : 0;
        return (
          <>
            <Col lg={10}>
              <div className="text-center">

                            {/* Winner Theme Suggestion Section (now rendered via function) */}
                            {/* {renderWinnerThemeSection()} */}

                              {/* Separator */}
                              {/* <div className="custom-separator my-5">
                                <div className="separator-end"></div>
                                <div className="separator-middle"></div>
                                <div className="separator-end right"></div>
                              </div> */}
             

                <h2 className="text-center mb-1">Contest Ended</h2>
                {contestInfo.winners && contestInfo.winners.length > 0 ? (
                  <>
                    {/* General Contest Stats (Participants, Voters, Avg Votes) */}
                    <Row className="justify-content-center mt-5 mb-5">
  <Col md={3} xs={3} className="text-center">
    {/* Image + Number Overlay */}
    <div className="position-relative d-inline-block">
      <img
        src="/images/contest/contest-banner-test.webp"
        alt="Participants"
        className="img-fluid rounded"
        style={{ width: '75px', height: 'auto' }}
      />
      <div className="position-absolute top-50 start-50 translate-middle text-white">
        <div className="h3 mb-0">
          {contestInfo.stats?.participants || 'N/A'}
        </div>
      </div>
    </div>
    {/* Label outside (below) the image */}
    <div className="mt-2 small">Participants</div>
  </Col>

  <Col md={3} xs={3} className="text-center">
    {/* Image + Number Overlay */}
    <div className="position-relative d-inline-block">
      <img
        src="/images/contest/contest-banner-test.webp"
        alt="Eligible Voters"
        className="img-fluid rounded"
        style={{ width: '75px', height: 'auto' }}
      />
      <div className="position-absolute top-50 start-50 translate-middle text-white">
        <div className="h3 mb-0">
          {contestInfo.stats?.voter_count || 'N/A'}
        </div>
      </div>
    </div>
    {/* Label outside (below) the image */}
    <div className="mt-2 small">Eligible Voters</div>
  </Col>

  <Col md={3} xs={3} className="text-center">
    {/* Image + Number Overlay */}
    <div className="position-relative d-inline-block">
      <img
        src="/images/contest/contest-banner-test.webp"
        alt="Avg Votes per Voter"
        className="img-fluid rounded"
        style={{ width: '75px', height: 'auto' }}
      />
      <div className="position-absolute top-50 start-50 translate-middle text-white">
        <div className="h3 mb-0">
          {contestInfo.stats?.avg_votes_per_voter.toFixed(1) || 'N/A'}
        </div>
      </div>
    </div>
    {/* Label outside (below) the image */}
    <div className="mt-2 small">Votes/Voter</div>
  </Col>
</Row>





                    {/* === Layout for a SINGLE WINNER === */}
                    {contestInfo.winners.length === 1 &&
                      contestInfo.winners.map((winner) => (
                        // For a single winner, we want the Row containing the card and voters
                        // to take up the available width, similar to how the original structure behaved.
                        // The my-3 provides spacing similar to the original m-3.
                        // The Row itself will be full width of its parent (div.text-center),
                        // and the Col md={6} children will divide that space.
                        <Row key={winner.id} className="my-3"> {/* Removed justify-content-center and the wrapping Col */}
                          {/* Winning Card */}
                          <Col md={6} className="text-center mb-3 mb-md-0 contest-undercard">
                            <h5>Winner Card</h5>
                            <Link
                              to={buildUrlWithParams({
                                ...Object.fromEntries(new URLSearchParams(window.location.search)),
                                selectedCard: winner.cardId,
                              })}
                              preventScrollReset={true}
                              style={{ textDecoration: 'none', zIndex: '2' }}
                            >
                              <img
                                src={`/cards/${winner.cardId2}.webp`}
                                alt={`Winning card by ${winner.username}`}
                                className="submission-card img-fluid mb-2"
                                style={{ width: '100%', maxWidth: '300px' }}
                              />
                            </Link>
                            <p className="contest-ribbon">
                              <Link to={`/user/${winner.id}`}>{winner.username}</Link>
                            </p>
                          </Col>

                          {/* Voters for this Winner's Card AND All Eligible Voters */}
                          <Col md={6}>
                            <h5>Winner Card Voters</h5>
                            {winner.voters && winner.voters.length > 0 ? (
                              <ul className="list-inline">
                                {winner.voters.map((voter) => (
                                  <li key={voter.id} className="list-inline-item">
                                    <Link to={`/user/${voter.id}`}>{voter.username}</Link>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No eligible votes for this card.</p>
                            )}

                            {/* All Eligible Voters (as per original single-winner layout) */}
                            {contestInfo.stats && (
                              <div className="stats-section mt-5">
                                <h5>All Eligible Voters</h5>
                                {contestInfo.stats.voters && contestInfo.stats.voters.length > 0 ? (
                                  <ul className="list-inline">
                                    {contestInfo.stats.voters.map((voter) => (
                                      <li key={voter.id} className="list-inline-item">
                                        <Link to={`/user/${voter.id}`}>{voter.username}</Link>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p>No eligible voters.</p>
                                )}
                              </div>
                            )}
                          </Col>
                          {/* {renderSubmissionStats()} */}
                        </Row>
                      ))}

                    {/* === Layout for MULTIPLE WINNERS (e.g., 2 or more) === */}
                    {contestInfo.winners.length > 1 && (
                      <>
                                                {/* Row for Winner Cards and their specific voters (side-by-side) */}
                        {/* Bootstrap Row is already display: flex; align-items: stretch by default usually handles Col height equalization */}
                        <Row className="justify-content-center mb-4">
                          {contestInfo.winners.map((winner) => (
                            <Col
                              key={winner.id}
                              md={6}
                              lg={contestInfo.winners.length === 2 ? 6 : (contestInfo.winners.length === 3 ? 4 : 4)}
                              className="mb-4 text-center d-flex flex-column" // 1. Make the Col a flex container, direction column
                            >
                              {/* This div wraps the card image and name. It will grow to fill available space. */}
                              <div className="flex-grow-1 contest-undercard"> {/* 2. Allow this part to grow */}
                                <h5 className="pb-3">Tied Winner Card</h5>
                                <Link
                                  to={buildUrlWithParams({
                                    ...Object.fromEntries(new URLSearchParams(window.location.search)),
                                    selectedCard: winner.cardId,
                                  })}
                                  preventScrollReset={true}
                                  style={{ textDecoration: 'none', zIndex: '2' }}
                                >
                                  <img
                                    src={`/cards/${winner.cardId2}.webp`}
                                    alt={`Winning card by ${winner.username}`}
                                    className="submission-card img-fluid mb-2" // img-fluid is important for responsiveness
                                    style={{ maxWidth: '300px' }}
                                  />
                                </Link>
                                <p className="contest-ribbon">
                                  <Link to={`/user/${winner.id}`}>{winner.username}</Link>
                                </p>
                              </div>

                              {/* Voters for THIS specific winner's card. This will be positioned after the growing block above. */}
                              <div className="mt-3"> {/* Spacing from the content above */}
                                <h5>Voters for this Card</h5>
                                {winner.voters && winner.voters.length > 0 ? (
                                  <ul className="list-inline">
                                    {winner.voters.map((voter) => (
                                      <li key={voter.id} className="list-inline-item">
                                        <Link to={`/user/${voter.id}`}>{voter.username}</Link>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="small">No eligible votes recorded for this card.</p>
                                )}
                              </div>
                            </Col>
                          ))}
                        </Row>

                        {/* Row for All Eligible Voters in the Contest (displayed below all winner cards) */}
                        {contestInfo.stats && (
                          <Row className="justify-content-center mt-4">
                            <Col md={10} className="text-center">
                              <h5>All Eligible Voters in the Contest</h5>
                              {contestInfo.stats.voters && contestInfo.stats.voters.length > 0 ? (
                                <ul className="list-inline">
                                  {contestInfo.stats.voters.map((voter) => (
                                    <li key={voter.id} className="list-inline-item">
                                      <Link to={`/user/${voter.id}`}>{voter.username}</Link>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p>No eligible voters recorded for the contest.</p>
                              )}
                            </Col>
                            {/* {renderSubmissionStats()} */}
                          </Row>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Alert variant="info" className="d-inline-block">
                    <p className="mb-0">No winner has been announced.</p>
                  </Alert>
                )}
              </div>




              <div className="custom-separator my-5">
                <div className="separator-end"></div>
                <div className="separator-middle"></div>
                <div className="separator-end right"></div>
              </div>

            </Col>

            <h3 className="mb-1">All Submissions</h3>
            <p className="text-center lead pb-3">Presented in order of highest to lowest rating</p>
            <CardList
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
              onVote={currentPhase === 'voting' && user ? handleVote : null}
              settings={settings}
            />


    {themeCards.length > 0 && (
      <>
        <Col lg={10}>
          <div className="custom-separator my-5">
            <div className="separator-end"></div>
            <div className="separator-middle"></div>
            <div className="separator-end right"></div>
          </div>
        </Col>
        <Col lg={9} className="mt-1 pb-3">
          <h2 className="text-center mb-3">Next Week's Contest</h2>
          <Row>
            {themeCards.map((theme) => (
              <Col md={4} key={theme.id} className="mb-4">
                <div
                  className={`contest-theme-styling theme-card d-flex flex-column h-100 text-center p-3 ${
                    theme.votes === maxVotes ? 'contest-theme-styling-winner' : ''
                  }`}
                >
                  <img
                    src={`/images/contest/sketch/${theme.image ? theme.image : "2"}.svg`}
                    className="w-100"
                    alt="theme"
                  />
                  <hr />
                  <h5 className="my-1">{theme.theme}</h5>
                  <div className="mt-auto">
                    <p className="m-0">Votes: {theme.votes}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </>
    )}

          </>
        );


      default:
        return null;
    }
  };

  if (!contestInfo) {
    return <div>Loading...</div>;
  }

  if (currentPhase === 'not_started' || contestInfo.start_date === 0) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Row className="align-items-center mb-4">
              <Col md={4} className="text-center">
                <img
                  src={`/images/contest/sketch/${contestInfo.id}.svg`}
                  alt="Contest"
                  className="img-fluid mb-md-0 mb-2 mt-4 mt-md-1"
                  style={{ padding: '5px' }}
                />
              </Col>
              <Col md={8}>
                <h4 className="text-center text-md-start">YOU HAVE AWAKENED ME TOO SOON, EXECUTUS!</h4>
                <p className="text-muted text-md-start">{randomMessage}</p>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    );
  }


  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col lg={10} className="contest-dirt-tl">
        <div class="ribbon-container d-none d-md-inline-flex">
<div class="ribbon-text">{contestInfo.title}</div>
</div>

          <h1 className="text-center mb-1 d-md-none d-block">{contestInfo.title}</h1>


          
          {contestInfo.idea && (
            <p className="text-center text-muted mb-4 d-none d-md-block" style={{ position: 'relative', marginTop: '-30px', zIndex: '5' }}>
              Idea by <Link to={`/user/${contestInfo.idea.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>{contestInfo.idea.username}</Link>
            </p>
          )}
          {contestInfo.idea && (
            <p className="text-center text-muted mb-4 d-md-none d-block">
              Idea by <Link to={`/user/${contestInfo.idea.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>{contestInfo.idea.username}</Link>
            </p>
          )}
          <Row className="align-items-center mb-4 contest-dirt-br">
            <Col md={4} className="text-center">
              <img
                src={`/images/contest/sketch/${contestInfo.id}.svg`}
                alt="Contest"
                className="img-fluid mb-md-0 mb-2 mt-4 mt-md-1"
                style={{ padding: '5px' }}
              />
            </Col>
            <Col md={8}>
              <ContestPhaseIndicator
                phase={currentPhase}
                startDate={contestInfo.start_date}
                submissionsEndDate={contestInfo.submissions_end_date}
                endDate={contestInfo.end_date}
              />
              <p className="lead text-center text-md-start" dangerouslySetInnerHTML={{ __html: contestInfo.description }}></p>
            </Col>
          </Row>

          {/* <hr className="my-5" style={{ borderWidth: '3px' }} /> */}
          {/* <img src="https://www.hearthcards.net/images/contest/separator.webp" className="img-fluid my-3 mb-5" /> */}
          <div className="custom-separator my-5">
            <div class="separator-end"></div><div class="separator-middle"></div><div class="separator-end right"></div>
          </div>
        </Col>

        {renderPhaseContent()}

        <p className="mb-5"></p>

      </Row>

      {selectedCard && (
        <CardModal
          card={selectedCard.id}
          onClose={() => setSelectedCard(null)}
          show={!!selectedCard}
        />
      )}
      {showCardPicker && (
        <CardPicker
          type="contest"
          onConfirm={(selectedCards) => {
            if (selectedCards.length > 0) {
              handleSubmit(selectedCards[0]);
            }
            setShowCardPicker(false);
          }}
          onClose={() => setShowCardPicker(false)}
          limit={1}
        />
      )}
    </Container>
  );
};

export default Contest;